
import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig, AxiosError } from 'axios';
import { store } from '../store';
import { setAuthUser } from '../store/authReducer';
import { API_AUTH } from "./constants";

class HttpService {
    private http: AxiosInstance;
    private API_BASE_URL = process.env.REACT_APP_API_BASEURL;

    constructor() {
        this.http = axios.create({
            baseURL: this.API_BASE_URL || '',
            headers: {
                'Content-Type': 'application/json',
            },

        });

        this.setupInterceptors();
    }

    private setupInterceptors() {
        // Request interceptor (for adding token + logging user activity)
        this.http.interceptors.request.use(
            async (config) => {
                const state = store.getState();
                const token = state.auth.authToken;
                if (token) {
                    if (!config.headers) {
                        config.headers = new axios.AxiosHeaders();
                    }
                    config.headers.Authorization = `Bearer ${token}`;
                }

                // if (!config.url?.includes('/user_activity/add')) {
                //     try {
                //         const username = state.auth.authUser?.username || 'unknown_user';
                //         const project = localStorage.getItem('project') || 'content_rocket';

                //         const activity = {
                //             action: `${(config.method || 'GET').toUpperCase()} ${config.url}`,
                //             page: window.location.pathname,
                //             project,
                //             username,
                //             created: new Date().toISOString(),
                //             request_time: new Date().toISOString(),
                //             url: window.location.href,
                //             metadata: JSON.stringify(config.data || {}),
                //         };

                //         // user activity API
                //         fetch(`${this.API_BASE_URL}/user_activity/add`, {
                //             method: 'POST',
                //             headers: {
                //                 'Content-Type': 'application/json',
                //                 Authorization: token ? `Bearer ${token}` : '',
                //             },
                //             body: JSON.stringify(activity),
                //         }).catch(() => {
                //             console.warn('User activity log failed.');
                //         });
                //     } catch (e) {
                //         console.warn('Error while logging user activity:', e);
                //     }
                // }

                return config;
            },
            (error) => Promise.reject(error)
        );

        // Response interceptor
        this.http.interceptors.response.use(
            (response: AxiosResponse) => this.handleResponse(response),
            (error: AxiosError) => this.handleErrorResponse(error)
        );

    }

    private async handleResponse(response: AxiosResponse): Promise<any> {
        const { status, statusText } = response;
        if ([401, 403].includes(status)) {
            return this.refreshToken(response);
        }
        if ([500, 502, 503, 504, 404].includes(status)) {
            return Promise.reject(statusText || 'Server error');
        }
        return response.data || response;
    }

    private async refreshToken(response: AxiosResponse): Promise<any> {
        const state = store.getState();
        const token = state.auth.authToken;

        if (!token) {
            window.location.href = '/';
            return Promise.reject(new Error('No refresh token available, redirecting to login'));
        }

        try {
            const res = await axios.get(`${this.API_BASE_URL}${API_AUTH.REFRESH}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            store.dispatch(setAuthUser({ ...res.data }));

            const originalRequest = response.config;
            originalRequest.headers.Authorization = `Bearer ${res.data.token}`;
            return axios(originalRequest);
        } catch (err) {
            window.location.href = '/';
            return Promise.reject(new Error('Unauthorized, please log in again'));
        }
    }

    private handleError(error: AxiosError): Promise<any> {
        return Promise.reject(error);
    }

    private handleErrorResponse(error: AxiosError): Promise<any> {
        if (error.response) {
            const { status, data } = error.response;
            if ([401, 403].includes(status)) {
                return this.refreshToken(error.response);
            } else if ([500, 502, 503, 504, 404, 422].includes(status)) {
                return Promise.reject(data || 'Server error');
            }
        }

        return Promise.reject(error);
    }

    // Common API methods
    public async getRequest(url: string) {
        return await this.http.get(url);
    }

    public async postRequest(url: string, payload: any) {
        return await this.http.post(url, payload);
    }

    public async postRequestFormData(url: string, payload: any) {
        return await this.http.post(url, payload, {
            headers: { "Content-Type": "multipart/form-data" },
        });
    }
    public async postRequestFormDataBlob(url: string, payload: any) {
        return await this.http.post(url, payload, {
            headers: { "Content-Type": "multipart/form-data" },
            responseType: "blob"
        });
    }

    public async postRequestBlob(url: string, payload: any) {
        return await this.http.post(url, payload, { responseType: "blob" });
    }

    public async putRequest(url: string, payload: any) {
        return await this.http.put(url, payload);
    }

    public async deleteRequest(url: string) {
        return await this.http.delete(url);
    }

    public async getRequestBlob(url: string) {
        return await this.http.get(url, { responseType: "blob" });
    }
}
export default new HttpService();

